import React, { useState } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';
import { useNavigate } from 'react-router-dom';
import { useRebalancing, RebalancingSuggestion, EligibleStock } from './useRebalancing';

/* ---------------------------
   Styling Constants
   --------------------------- */

const ACTION_CONFIG: Record<string, { label: string; bg: string; color: string; icon: string }> = {
    ACHETER: { label: 'À explorer', bg: '#dcfce7', color: '#16a34a', icon: '🟢' },
    RENFORCER: { label: 'Tendance haussière', bg: '#dbeafe', color: '#2563eb', icon: '🔵' },
    CONSERVER: { label: 'Équilibré', bg: '#f3f4f6', color: '#6b7280', icon: '⚪' },
    REDUIRE: { label: 'Surexposition', bg: '#fef3c7', color: '#d97706', icon: '🟠' },
};

/* ---------------------------
   Helper Components
   --------------------------- */

const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
    const config = ACTION_CONFIG[action] || ACTION_CONFIG.CONSERVER;
    return (
        <span
            style={{
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: config.bg,
                color: config.color,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
            }}
        >
            {config.icon} {config.label}
        </span>
    );
};

const ValueTiltGauge: React.FC<{ score: number | null }> = ({ score }) => {
    if (score === null) return <span style={{ color: '#9ca3af' }}>-</span>;

    const getColor = (s: number) => {
        if (s >= 70) return '#10b981';
        if (s >= 50) return '#f59e0b';
        if (s >= 30) return '#f97316';
        return '#ef4444';
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
                style={{
                    width: '60px',
                    height: '8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        width: `${score}%`,
                        height: '100%',
                        backgroundColor: getColor(score),
                        transition: 'width 0.3s ease',
                    }}
                />
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: getColor(score) }}>
                {score.toFixed(0)}
            </span>
        </div>
    );
};

const SuggestionCard: React.FC<{ suggestion: RebalancingSuggestion }> = ({ suggestion }) => {
    const navigate = useNavigate();

    return (
        <div
            style={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                marginBottom: '12px',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
            }}
            onClick={() => navigate(`/data?symbol=${suggestion.symbole}`)}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                        {suggestion.symbole}
                    </span>
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#64748b' }}>
                        {suggestion.nom_societe}
                    </span>
                </div>
                <ActionBadge action={suggestion.action} />
            </div>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Score Value Tilt</div>
                    <ValueTiltGauge score={suggestion.score_value_tilt} />
                </div>
                {suggestion.decote_estimee !== null && (
                    <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Décote estimée</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: suggestion.decote_estimee > 0 ? '#10b981' : '#ef4444' }}>
                            {suggestion.decote_estimee > 0 ? '+' : ''}{suggestion.decote_estimee.toFixed(0)}%
                        </div>
                    </div>
                )}
                {suggestion.montant_suggere !== null && suggestion.montant_suggere > 0 && (
                    <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Montant indicatif</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                            {suggestion.montant_suggere.toLocaleString()} FCFA
                        </div>
                    </div>
                )}
                <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Poids actuel → cible</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                        {(suggestion.poids_actuel ?? 0).toFixed(1)}% → {(suggestion.poids_cible ?? 0).toFixed(1)}%
                    </div>
                </div>
            </div>

            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', fontStyle: 'italic' }}>
                💡 {suggestion.justification}
            </div>
        </div>
    );
};

const AllocationChart: React.FC<{ title: string; current: Record<string, number>; projected: Record<string, number> }> = ({
    title,
    current,
    projected,
}) => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(projected)]));
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

    return (
        <Box>
            <div style={{ padding: '16px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{title}</h4>
                <div style={{ display: 'flex', gap: '20px' }}>
                    {/* Current */}
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>Actuel</div>
                        {allKeys.map((key, i) => (
                            <div key={key} style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                                    <span style={{ color: '#475569' }}>{key}</span>
                                    <span style={{ fontWeight: '500' }}>{(current[key] || 0).toFixed(1)}%</span>
                                </div>
                                <div style={{ height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px' }}>
                                    <div
                                        style={{
                                            width: `${Math.min(100, current[key] || 0)}%`,
                                            height: '100%',
                                            backgroundColor: colors[i % colors.length],
                                            borderRadius: '3px',
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Projected */}
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>Projeté</div>
                        {allKeys.map((key, i) => (
                            <div key={key} style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                                    <span style={{ color: '#475569' }}>{key}</span>
                                    <span style={{ fontWeight: '500' }}>{(projected[key] || 0).toFixed(1)}%</span>
                                </div>
                                <div style={{ height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px' }}>
                                    <div
                                        style={{
                                            width: `${Math.min(100, projected[key] || 0)}%`,
                                            height: '100%',
                                            backgroundColor: colors[i % colors.length],
                                            borderRadius: '3px',
                                            opacity: 0.7,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Box>
    );
};

/* ---------------------------
   Main Screen
   --------------------------- */

const RebalancingScreen: React.FC = () => {
    const { data, loading, error, refetch } = useRebalancing();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'suggestions' | 'universe' | 'strategy'>('suggestions');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'score_value_tilt', direction: 'desc' });

    if (loading) {
        return (
            <SiteLayout>
                <Header title="Rééquilibrage Intelligent" />
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚖️</div>
                    <div style={{ color: '#64748b' }}>Analyse de votre portefeuille en cours...</div>
                </div>
            </SiteLayout>
        );
    }

    if (error) {
        return (
            <SiteLayout>
                <Header title="Rééquilibrage Intelligent" />
                <Box>
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                        <div style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</div>
                        <button
                            onClick={refetch}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                            }}
                        >
                            Réessayer
                        </button>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    if (!data || data.portfolio_summary.total_value === 0) {
        return (
            <SiteLayout>
                <Header title="Rééquilibrage Intelligent" />
                <Box>
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>💼</div>
                        <h3 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Votre portefeuille est vide</h3>
                        <p style={{ color: '#64748b', marginBottom: '20px' }}>
                            {data?.message || 'Créditez votre compte et simulez des opérations pour accéder aux observations de rééquilibrage.'}
                        </p>
                        <button
                            onClick={() => navigate('/transactions')}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                            }}
                        >
                            Accéder aux simulations
                        </button>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    // Group suggestions by action type
    const buyActions = data.suggestions.filter((s) => s.action === 'OPPORTUNITÉ');
    const reinforceActions = data.suggestions.filter((s) => s.action === 'TENDANCE_HAUSSIÈRE');
    const reduceActions = data.suggestions.filter((s) => s.action === 'SUREXPOSITION');
    const holdActions = data.suggestions.filter((s) => s.action === 'ÉQUILIBRÉ');

    const toggleFilter = (filter: string) => {
        setActiveFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
    };

    const eligibleStocks = data.eligible_stocks || [];
    const filteredUniverse = eligibleStocks.filter(stock => {
        const matchesSearch = (stock.nom_societe?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (stock.symbole?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (stock.secteur?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (activeFilters.includes('decote20') && (stock.decote_estimee || 0) < 20) return false;
        if (activeFilters.includes('score70') && (stock.score_value_tilt || 0) < 70) return false;
        if (activeFilters.includes('undervalued') && (stock.decote_estimee || 0) <= 0) return false;

        return true;
    });

    const sortedUniverse = [...filteredUniverse].sort((a, b) => {
        if (!sortConfig) return 0;

        const { key, direction } = sortConfig;

        // @ts-ignore - dynamic access
        const valA = a[key] ?? -999;
        // @ts-ignore - dynamic access
        const valB = b[key] ?? -999;

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    const requestSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'desc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) return <span style={{ opacity: 0.3 }}>⇅</span>;
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    return (
        <SiteLayout>
            <Header title="Rééquilibrage Intelligent" />
            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Disclaimer */}
                <Disclaimer type="investment" />

                {/* Portfolio Summary */}
                <Box>
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                            📊 Résumé de votre portefeuille
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>Valeur totale</div>
                                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                                    {data.portfolio_summary.total_value.toLocaleString()} FCFA
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>Cash disponible</div>
                                <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                                    {data.portfolio_summary.cash_available.toLocaleString()} FCFA
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    {data.portfolio_summary.cash_percentage.toFixed(1)}% du portefeuille
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>Positions</div>
                                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                                    {data.portfolio_summary.holdings_count}
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    Cible: {data.portfolio_summary.target_holdings}
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '20px', marginBottom: '16px' }}>
                    <button
                        onClick={() => setActiveTab('suggestions')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'suggestions' ? '#2563eb' : '#f1f5f9',
                            color: activeTab === 'suggestions' ? 'white' : '#475569',
                            fontWeight: '500',
                            cursor: 'pointer',
                        }}
                    >
                        📋 Observations
                    </button>
                    <button
                        onClick={() => setActiveTab('universe')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'universe' ? '#2563eb' : '#f1f5f9',
                            color: activeTab === 'universe' ? 'white' : '#475569',
                            fontWeight: '500',
                            cursor: 'pointer',
                        }}
                    >
                        🌐 Univers ({data.eligible_stocks?.length || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('strategy')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'strategy' ? '#2563eb' : '#f1f5f9',
                            color: activeTab === 'strategy' ? 'white' : '#475569',
                            fontWeight: '500',
                            cursor: 'pointer',
                        }}
                    >
                        ℹ️ Stratégie Applicable
                    </button>
                </div>

                {activeTab === 'suggestions' && (
                    <>
                        {/* Suggestions Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                            {/* Buy & Reinforce */}
                            <div>
                                {buyActions.length > 0 && (
                                    <>
                                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a', marginBottom: '12px' }}>
                                            🟢 Valeurs à explorer ({buyActions.length})
                                        </h4>
                                        {buyActions.map((s) => (
                                            <SuggestionCard key={s.symbole} suggestion={s} />
                                        ))}
                                    </>
                                )}
                                {reinforceActions.length > 0 && (
                                    <>
                                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2563eb', marginTop: '20px', marginBottom: '12px' }}>
                                            🔵 Tendance haussière détectée ({reinforceActions.length})
                                        </h4>
                                        {reinforceActions.map((s) => (
                                            <SuggestionCard key={s.symbole} suggestion={s} />
                                        ))}
                                    </>
                                )}
                            </div>

                            {/* Hold & Reduce */}
                            <div>
                                {reduceActions.length > 0 && (
                                    <>
                                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#d97706', marginBottom: '12px' }}>
                                            🟠 Concentration élevée ({reduceActions.length})
                                        </h4>
                                        {reduceActions.map((s) => (
                                            <SuggestionCard key={s.symbole} suggestion={s} />
                                        ))}
                                    </>
                                )}
                                {holdActions.length > 0 && (
                                    <>
                                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginTop: '20px', marginBottom: '12px' }}>
                                            ⚪ Positions équilibrées ({holdActions.length})
                                        </h4>
                                        {holdActions.map((s) => (
                                            <SuggestionCard key={s.symbole} suggestion={s} />
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Impact Analysis */}
                        <h3 style={{ margin: '30px 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                            📈 Impact sur la diversification
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                            <AllocationChart
                                title="Allocation Sectorielle"
                                current={data.sector_impact.current}
                                projected={data.sector_impact.projected}
                            />
                            <AllocationChart
                                title="Allocation Géographique"
                                current={data.country_impact.current}
                                projected={data.country_impact.projected}
                            />
                        </div>
                    </>
                )}

                {activeTab === 'universe' && (
                    <Box>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                                    Top actions ({filteredUniverse.length})
                                </h4>

                                <div style={{ display: 'flex', gap: '16px', flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', padding: '6px 16px', borderRadius: '24px', border: '1px solid #e2e8f0', minWidth: '200px' }}>
                                        <span style={{ marginRight: '8px' }}>🔍</span>
                                        <input
                                            type="text"
                                            placeholder="Rechercher..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {[
                                            { id: 'undervalued', label: '💎 Décoté' },
                                            { id: 'decote20', label: '🔥 Décote > 20%' },
                                            { id: 'score70', label: '⭐ Score > 70' }
                                        ].map(f => (
                                            <button
                                                key={f.id}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '20px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: activeFilters.includes(f.id) ? '#eff6ff' : 'white',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    color: activeFilters.includes(f.id) ? '#2563eb' : '#64748b',
                                                    fontWeight: activeFilters.includes(f.id) ? '600' : 'normal',
                                                    transition: 'all 0.2s',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onClick={() => toggleFilter(f.id)}
                                            >
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                                            <th style={{ padding: '12px 8px' }}>Symbole</th>
                                            <th style={{ padding: '12px 8px' }}>Société</th>
                                            <th style={{ padding: '12px 8px' }}>Secteur</th>
                                            <th style={{ padding: '12px 8px' }}>Pays</th>
                                            <th
                                                style={{ padding: '12px 8px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => requestSort('score_value_tilt')}
                                            >
                                                Score {getSortIcon('score_value_tilt')}
                                            </th>
                                            <th
                                                style={{ padding: '12px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => requestSort('decote_estimee')}
                                            >
                                                Décote {getSortIcon('decote_estimee')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedUniverse.map((stock) => (
                                            <tr
                                                key={stock.symbole}
                                                style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                                onClick={() => navigate(`/data?symbol=${stock.symbole}`)}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                <td style={{ padding: '12px 8px', fontWeight: '600' }}>{stock.symbole}</td>
                                                <td style={{ padding: '12px 8px', color: '#64748b' }}>{stock.nom_societe}</td>
                                                <td style={{ padding: '12px 8px' }}>{stock.secteur}</td>
                                                <td style={{ padding: '12px 8px' }}>{stock.pays}</td>
                                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                    <ValueTiltGauge score={stock.score_value_tilt} />
                                                </td>
                                                <td
                                                    style={{
                                                        padding: '12px 8px',
                                                        textAlign: 'right',
                                                        fontWeight: '500',
                                                        color: (stock.decote_estimee ?? 0) > 0 ? '#10b981' : '#ef4444',
                                                    }}
                                                >
                                                    {stock.decote_estimee !== null
                                                        ? `${stock.decote_estimee > 0 ? '+' : ''}${stock.decote_estimee.toFixed(0)}%`
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Box>
                )}

                {activeTab === 'strategy' && (
                    <Box>
                        <div style={{ padding: '24px' }}>
                            <h3 style={{ marginTop: 0, color: '#1e293b' }}>Approche "Value Tilt" & Rééquilibrage</h3>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginTop: '20px' }}>
                                <div>
                                    <h4 style={{ color: '#2563eb', marginBottom: '10px' }}>1. Score Value Tilt (0-100)</h4>
                                    <p style={{ color: '#475569', lineHeight: '1.6' }}>
                                        Chaque action est notée selon plusieurs piliers fondamentaux :
                                    </p>
                                    <ul style={{ color: '#475569', lineHeight: '1.6', paddingLeft: '20px' }}>
                                        <li><strong>Sous-valorisation (40%)</strong> : Potentiel de hausse basé sur l'analyse DCF et les multiples boursiers.</li>
                                        <li><strong>Qualité Financière (25%)</strong> : Rentabilité (ROE), maîtrise de la dette et croissance des revenus.</li>
                                        <li><strong>Stabilité des Dividendes (15%)</strong> : Historique et régularité des versements aux actionnaires.</li>
                                        <li><strong>Liquidité (10%)</strong> : Facilité d'achat et de vente sur le marché quotidien.</li>
                                        <li><strong>Impact de Diversification (10%)</strong> : Bonus pour les actions qui améliorent l'équilibre sectoriel ou géographique de votre portefeuille.</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 style={{ color: '#16a34a', marginBottom: '10px' }}>2. Règles de Rééquilibrage</h4>
                                    <p style={{ color: '#475569', lineHeight: '1.6' }}>
                                        L'outil identifie des observations pour vous aider à analyser l'équilibre de votre portefeuille :
                                    </p>
                                    <ul style={{ color: '#475569', lineHeight: '1.6', paddingLeft: '20px' }}>
                                        <li><strong>Poids Max par Position</strong> : 15% pour éviter la concentration excessive sur un seul titre.</li>
                                        <li><strong>Tendance haussière</strong> : Détectée pour les titres de haute qualité (Score &gt; 70) que vous possédez déjà.</li>
                                        <li><strong>Surexposition</strong> : Détectée lorsqu'un titre dépasse son poids cible ou que ses fondamentaux se dégradent (Score &lt; 30).</li>
                                        <li><strong>Valeurs à explorer</strong> : Identification des actions à fort potentiel fondamental (Score &gt; 60) absentes de votre portefeuille.</li>
                                    </ul>
                                </div>
                            </div>

                            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', borderLeft: '4px solid #0ea5e9' }}>
                                <p style={{ margin: 0, color: '#0369a1', fontSize: '14px' }}>
                                    <strong>Note :</strong> Cette approche éducative combine l'analyse fondamentale (identifier les décotes) et l'étude de la diversification. Ceci ne constitue pas un conseil en investissement.
                                </p>
                            </div>
                        </div>
                    </Box>
                )}

                {/* Disclaimer Footer */}
                <div
                    style={{
                        marginTop: '30px',
                        padding: '16px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #fbbf24',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <div>
                            <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                                Avertissement
                            </div>
                            <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.5' }}>
                                {data.disclaimer}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SiteLayout>
    );
};

export default RebalancingScreen;
